/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { Row, Col, Layout, Drawer } from 'antd';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { map } from 'underscore';
import NotificationList from 'components/Notification/NotificationList';
import useIsGuest from 'core/guestBooking/useIsGuest';
import SubHeader from '../SubHeader';
import { HeaderLogo } from '../../Elements/CustomSVGIcon';
import Menu from '../Menu';
import type { HeaderProps } from './types';
import './header.css';
import Logout from '../../Logout';
import { CombinedStates } from '../../../core/types';
import { ToolbarItem } from '../Main/types';
import { ADMIN_ACCESS_LEVELS, ADMIN_USER, SCORCHSOFT_USER, SECURITY_USER, SUPER_ADMIN_USER } from '../../../core/utils/value';

const Header = (props:HeaderProps) => {
    const {
        subHeaderTitle,
        subHeaderHasBackLink,
        subHeaderLeftAction,
        subHeaderRightAction,
        subHeaderBackLinkCallback,
        subHeaderBackLink,
        subHeaderUseCloseIcon,
        hideSubHeader,
        menu,
        title,
        menuPlacement,
        menuWidth,
        floating,
        beforeHeader,
        afterHeader,
        toolbar,
    } = props;

    const {
        userData,
    } = useSelector((state:CombinedStates) => state.user);

    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const currentPath = location.pathname;
    // One-off guest bookings and the guest view/cancel page have no
    // authenticated user: hide logout / notifications and don't let the logo
    // navigate away (to login).
    const isGuest = useIsGuest();
    const allowedAccessLevels = [SECURITY_USER, SCORCHSOFT_USER, ADMIN_USER, SUPER_ADMIN_USER];
    const fallbackUrl = ADMIN_ACCESS_LEVELS.includes(userData?.role?.accessLevel) ? '/dashboard' : '/security-dashboard';

    const handleMobileNavClick = (link: string) => {
        navigate(link);
        setMobileMenuOpen(false);
    };

    const toolbarItems = toolbar ? map(toolbar, (item:unknown, index) => {
        const i = index;
        const className = (item as ToolbarItem).isActive || currentPath === (item as ToolbarItem).link ? 'active' : '';
        return (
            <span
                key={i}
                className={`toolbar-link ${className}`}
                data-tour={(item as ToolbarItem).tourId}
                onClick={() => navigate((item as ToolbarItem).link)}>
                {(item as ToolbarItem).label}
            </span>
        );
    }) : null;

    const mobileMenuItems = toolbar ? map(toolbar, (item:unknown, index) => {
        const i = index;
        const className = (item as ToolbarItem).isActive || currentPath === (item as ToolbarItem).link ? 'active' : '';
        return (
            <div
                key={i}
                className={`mobile-menu-item ${className}`}
                // Suffixed so it can never be confused with the desktop anchor:
                // the mobile Drawer renders into a portal and stays mounted once
                // opened, so an identical value could win the querySelector.
                data-tour={(item as ToolbarItem).tourId ? `${(item as ToolbarItem).tourId}-mobile` : undefined}
                onClick={() => handleMobileNavClick((item as ToolbarItem).link)}>
                {(item as ToolbarItem).label}
            </div>
        );
    }) : null;

    return (
        <div className={`header ${floating ? 'floating-header' : ''}`}>
            {beforeHeader}
            <Layout.Header className="header__inner">
                <Row align="middle" style={{ height: 'inherit', width: '100%', maxWidth: 1320, margin: '0 auto' }}>
                    {/* Hamburger menu - mobile only, before logo. Hidden for
                        guests, who have no toolbar to open. */}
                    {!isGuest && (
                        <Col flex="none" className="mobile-hamburger">
                            <div
                                className="hamburger-btn"
                                onClick={() => setMobileMenuOpen(true)}>
                                <MenuOutlined />
                            </div>
                        </Col>
                    )}

                    {/* Logo - fixed size, never shrink. Guests can't navigate away. */}
                    <Col flex="none" className="logo-col">
                        <Row align="middle" style={{ cursor: isGuest ? 'default' : 'pointer' }} onClick={() => !isGuest && navigate(fallbackUrl)}>
                            <HeaderLogo />
                        </Row>
                    </Col>

                    {/* Toolbar/Menu - desktop only */}
                    <Col flex="auto" className="desktop-nav">
                        <Row align="middle" justify="start">
                            <Col style={{ width: '100%' }}>
                                {toolbarItems ? (
                                    <nav className="header-custom-links">
                                        {toolbarItems}
                                    </nav>
                                ) : null }
                                {menu ? <Menu items={menu} placement={menuPlacement} width={menuWidth} /> : null}
                            </Col>
                        </Row>
                    </Col>

                    {/* Notifications and Logout. Hidden for unauthenticated guests. */}
                    <Col flex="none" className="header-actions-col">
                        <div className="header-actions">
                            {(!isGuest && userData?.role?.accessLevel !== SECURITY_USER) ? (
                                <NotificationList />
                            ) : null }
                            {!isGuest && <Logout />}
                        </div>
                    </Col>
                </Row>
            </Layout.Header>

            {/* Mobile Menu Drawer */}
            <Drawer
                title="Menu"
                placement="left"
                onClose={() => setMobileMenuOpen(false)}
                open={mobileMenuOpen}
                className="mobile-menu-drawer"
                width={280}>
                <nav className="mobile-menu-nav">
                    {mobileMenuItems}
                </nav>
            </Drawer>
            { hideSubHeader ? null : (
                <SubHeader
                    title={subHeaderTitle}
                    hasBackLink={subHeaderHasBackLink}
                    backLinkCallback={subHeaderBackLinkCallback}
                    backLink={subHeaderBackLink}
                    useCloseIcon={subHeaderUseCloseIcon}
                    leftAction={subHeaderLeftAction}
                    rightAction={subHeaderRightAction} />
            )}
            {afterHeader}
        </div>
    );
};

Header.defaultProps = {
    logo: '',
    beforeHeader: null,
    afterHeader: null,
    subHeaderTitle: '',
    subHeaderHasBackLink: false,
    subHeaderUseCloseIcon: false,
    subHeaderLeftAction: '',
    subHeaderRightAction: '',
    subHeaderBackLinkCallback: () => console.log('Back'),
    subHeaderBackLink: '#',
    hideSubHeader: false,
};

export default Header;
